import { zodResolver } from "@hookform/resolvers/zod";
import { designTokens, RiCircleFill } from "@itandi/itandi-bb-ui";
import { Alertv2 } from "@itandi/itandi-bb-ui/Alertv2";
import { Box } from "@itandi/itandi-bb-ui/Box";
import { Buttonv2 } from "@itandi/itandi-bb-ui/Buttonv2";
import { Divider } from "@itandi/itandi-bb-ui/Divider";
import { Flex } from "@itandi/itandi-bb-ui/Flex";
import { FloatingMenu } from "@itandi/itandi-bb-ui/FloatingMenu";
import { Grid } from "@itandi/itandi-bb-ui/Grid";
import { Heading } from "@itandi/itandi-bb-ui/Heading";
import { Label } from "@itandi/itandi-bb-ui/Label";
import { useNotification } from "@itandi/itandi-bb-ui/NotificationBarStore";
import { Panel } from "@itandi/itandi-bb-ui/Panel";
import { PanelBody } from "@itandi/itandi-bb-ui/PanelBody";
import { Paragraph } from "@itandi/itandi-bb-ui/Paragraph";
import { PDFViewer } from "@itandi/itandi-bb-ui/PDFViewer";
import { YokuaruBody } from "@itandi/itandi-bb-ui/YokuaruBody";
import { YokuaruFooter } from "@itandi/itandi-bb-ui/YokuaruFooter";
import { YokuaruHeader } from "@itandi/itandi-bb-ui/YokuaruHeader";
import { useRouter } from "next/router";
import { Fragment, useState } from "react";
import type { SubmitErrorHandler } from "react-hook-form";
import {
	FormProvider,
	useFieldArray,
	useForm,
	useFormContext,
	useWatch
} from "react-hook-form";
import useSWRMutation from "swr/mutation";
import { BackToEditConfirmModal } from "~/components/specifics/deals/[id]/estimations/ocr_requests/[requestId]/BackToEditConfirmModal";
import { EstimationOcrItemForm } from "~/components/specifics/deals/[id]/estimations/ocr_requests/[requestId]/EstimationOcrItemForm";
import type { NskResponse } from "~/models/Api";
import type { Deal } from "~/models/Deal";
import { formatPropertyName } from "~/models/Deal";
import type {
	DealEstimationOcrFormInput,
	DealEstimationOcrFormValues
} from "~/models/DealEstimationOcrForm";
import {
	buildDealEstimationOcrFormValues,
	buildEmptyDealEstimationOcrFormItem,
	calculateDealEstimationOcrAmounts,
	dealEstimationOcrFormSchema
} from "~/models/DealEstimationOcrForm";
import type { DealEstimationOcrRequest } from "~/models/DealEstimationOcrRequest";
import { isSuccess } from "~/utils/api/api_handler";
import {
	getFilePath,
	reflect
} from "~/utils/api/deals/DealEstimationOcrRequest";
import { toThousandSeparator } from "~/utils/CurrencySeparator";
import { generatePath } from "~/utils/generatePath";

type Props = {
	backToEditPath: string;
	deal: Deal;
	items: NonNullable<DealEstimationOcrRequest["items"]>;
	ocrRequest: Omit<DealEstimationOcrRequest, "items">;
};

const PDF_PANE_WIDTH = "45%";
const SUMMARY_MARKER_SIZE = "0.75rem";

function EstimationOcrFooterTotalAmount() {
	const { control } = useFormContext<DealEstimationOcrFormInput>();
	const items = useWatch({ control, name: "items" });
	const amounts = calculateDealEstimationOcrAmounts(items);

	return (
		<Flex alignItems="center" gap={designTokens["spacing/xs"]}>
			<Flex alignItems="center" gap={designTokens["spacing/xxs"]}>
				<Label size="s">総額：</Label>
				<Label size="l" weight="bold">
					{`¥${toThousandSeparator(amounts.totalAmountWithTax) ?? "0"}`}
				</Label>
			</Flex>
			<Label size="s">
				{`税抜：¥${toThousandSeparator(amounts.totalAmount) ?? "0"}`}
			</Label>
		</Flex>
	);
}

function EstimationOcrAmountsSummary() {
	const { control } = useFormContext<DealEstimationOcrFormInput>();
	const items = useWatch({ control, name: "items" });
	const amounts = calculateDealEstimationOcrAmounts(items);

	return (
		<Flex alignItems="center" gap={designTokens["spacing/m"]}>
			<Grid
				alignItems="center"
				columnGap={designTokens["spacing/xxs"]}
				gridTemplateColumns="auto auto auto"
				rowGap={designTokens["spacing/xs"]}
				width="fit-content"
			>
				<RiCircleFill
					color={designTokens["color/chips/presentation/foreground/red"]}
					size={SUMMARY_MARKER_SIZE}
				/>
				<Label size="s">提案項目を除く小計: </Label>
				<Flex justifyContent="flex-end">
					<Label size="s" weight="bold">
						{`¥${toThousandSeparator(amounts.nonProposalTotalAmount) ?? "0"}`}
					</Label>
				</Flex>
				<RiCircleFill
					color={designTokens["color/chips/presentation/foreground/blue"]}
					size={SUMMARY_MARKER_SIZE}
				/>
				<Label size="s">提案項目小計: </Label>
				<Flex justifyContent="flex-end">
					<Label size="s" weight="bold">
						{`¥${toThousandSeparator(amounts.proposalTotalAmount) ?? "0"}`}
					</Label>
				</Flex>
			</Grid>
		</Flex>
	);
}

export function SucceededState({
	backToEditPath,
	deal,
	items,
	ocrRequest
}: Props) {
	const router = useRouter();
	const { notify } = useNotification();
	const filePath = getFilePath(ocrRequest.dealId, ocrRequest.id);
	const [isLeaveConfirmModalOpen, setIsLeaveConfirmModalOpen] = useState(false);
	const [confirmedIds, setConfirmedIds] = useState<readonly string[]>([]);
	const methods = useForm<DealEstimationOcrFormInput>({
		defaultValues: buildDealEstimationOcrFormValues(items),
		mode: "onChange",
		resolver: zodResolver(dealEstimationOcrFormSchema)
	});
	const { fields, append, remove } = useFieldArray({
		control: methods.control,
		name: "items"
	});
	const { trigger, isMutating } = useSWRMutation<
		NskResponse,
		never,
		string,
		DealEstimationOcrFormValues
	>(
		generatePath(
			"/deals/:deal_id/deal_estimation_ocr_requests/:id/reflection",
			{ deal_id: ocrRequest.dealId, id: ocrRequest.id }
		),
		async (_key, { arg }) =>
			await reflect(ocrRequest.dealId, ocrRequest.id, arg)
	);

	const onInvalid: SubmitErrorHandler<DealEstimationOcrFormInput> = (
		formErrors
	) => {
		const invalidIds = Object.keys(formErrors.items ?? {}).map(
			(errorKey) => fields.at(Number(errorKey))?.id
		);
		setConfirmedIds((prev) => prev.filter((id) => !invalidIds.includes(id)));
	};

	const handleReflect = methods.handleSubmit(async (values) => {
		const response = await trigger(values as DealEstimationOcrFormValues);
		if (isSuccess(response)) {
			notify({
				content: "見積明細を反映しました。",
				variant: "success"
			});
			await router.push(backToEditPath);
		} else {
			notify({
				content:
					response.message ??
					"エラーが発生しました。時間をおいて再度お試しください。",
				variant: "error"
			});
		}
	}, onInvalid);
	const onConfirmationChange = (id: string) => {
		setConfirmedIds((prev) => {
			const index = prev.indexOf(id);
			return index === -1 ? prev.concat(id) : prev.toSpliced(index, 1);
		});
	};
	return (
		<FormProvider {...methods}>
			<Grid
				alignItems="start"
				gridTemplateColumns={`${PDF_PANE_WIDTH} minmax(0, 1fr)`}
				width="100%"
			>
				<Box height="100dvh" position="sticky" top={0}>
					<PDFViewer
						height="100%"
						src={filePath}
						title="見積書原本PDF"
						width="100%"
					/>
				</Box>
				<Box minWidth={0}>
					<YokuaruHeader
						heading={
							<Flex alignItems="center" gap={designTokens["spacing/s"]}>
								<Heading as="h1">読み取り結果の確認・修正</Heading>
								<Label color="subtle">{formatPropertyName(deal)}</Label>
							</Flex>
						}
						sticky
					/>
					<YokuaruBody>
						<Panel>
							<PanelBody>
								<Grid gap={designTokens["spacing/m"]}>
									<EstimationOcrAmountsSummary />
									<Alertv2 variant="info">
										<Paragraph>
											AIによる読み取り結果には誤りが含まれる場合があります。
											<br />
											原本PDFをご確認の上、必要に応じて修正してください。
										</Paragraph>
									</Alertv2>
									<Grid gap={designTokens["spacing/s"]}>
										{fields.map((field, index) => (
											<Fragment key={field.id}>
												{index > 0 && <Divider />}
												<EstimationOcrItemForm
													disabled={isMutating}
													index={index}
													isConfirmed={confirmedIds.includes(field.id)}
													onConfirmationChange={() => {
														onConfirmationChange(field.id);
													}}
													onRemove={remove}
												/>
											</Fragment>
										))}
									</Grid>
									<Flex justifyContent="flex-end">
										<Buttonv2
											disabled={isMutating}
											onClick={() => {
												append(buildEmptyDealEstimationOcrFormItem());
											}}
											variant="secondary"
										>
											明細を追加
										</Buttonv2>
									</Flex>
								</Grid>
							</PanelBody>
						</Panel>
					</YokuaruBody>
					<YokuaruFooter
						floatingMenu={
							<FloatingMenu
								fitToParent
								leftArea={<EstimationOcrFooterTotalAmount />}
								rightArea={
									<Flex alignItems="center" gap={designTokens["spacing/s"]}>
										<Buttonv2
											disabled={isMutating}
											onClick={() => {
												setIsLeaveConfirmModalOpen(true);
											}}
											variant="secondary"
										>
											見積作成画面へもどる
										</Buttonv2>
										<Buttonv2
											disabled={isMutating || fields.length === 0}
											loading={isMutating}
											onClick={() => {
												void handleReflect();
											}}
											variant="primary"
										>
											見積に反映
										</Buttonv2>
									</Flex>
								}
							/>
						}
					/>
				</Box>
				<BackToEditConfirmModal
					alertMessage={
						<>
							編集中の内容は保存されません。
							<br />
							今回の読み取り結果にもどることはできません。
						</>
					}
					backToEditPath={backToEditPath}
					isOpen={isLeaveConfirmModalOpen}
					onClose={() => {
						setIsLeaveConfirmModalOpen(false);
					}}
				/>
			</Grid>
		</FormProvider>
	);
}
